/**
 * Helpers for talking to the dashboard API from the BPMN editor.
 *
 * In production the editor lives on bpmn.weissenberg-group.de and nginx
 * proxies /api/dashboard/ to the FastAPI service in the same compose
 * project, so an empty base URL ("same-origin") is the correct default.
 * For local development you can override with REACT_APP_API_BASE.
 */

import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

export function resolveApiUrl(path) {
  const raw = String(path || '');
  if (!raw) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  const normalizedPath = raw.startsWith('/') ? raw : `/${raw}`;
  return `${API_BASE}${normalizedPath}`;
}

/** Acquire an access token for the dashboard API. */
async function getApiToken(msalInstance, account) {
  if (!msalInstance) throw new Error('MSAL ist nicht initialisiert');
  let resolvedAccount = account || null;

  if (!resolvedAccount) {
    const active = msalInstance.getActiveAccount?.() || null;
    const all = msalInstance.getAllAccounts?.() || [];
    resolvedAccount = active || all[0] || null;
    if (resolvedAccount && !active && msalInstance.setActiveAccount) {
      msalInstance.setActiveAccount(resolvedAccount);
    }
  }

  // If the React account state is temporarily empty, recover interactively.
  if (!resolvedAccount) {
    try {
      const loginResult = await msalInstance.loginPopup(loginRequest);
      resolvedAccount = loginResult?.account || null;
      if (resolvedAccount && msalInstance.setActiveAccount) {
        msalInstance.setActiveAccount(resolvedAccount);
      }
    } catch (err) {
      const code = err && (err.errorCode || err.name);
      if (code === 'interaction_in_progress') {
        const active = msalInstance.getActiveAccount?.() || null;
        const all = msalInstance.getAllAccounts?.() || [];
        resolvedAccount = active || all[0] || null;
        if (!resolvedAccount) {
          await msalInstance.loginRedirect(loginRequest);
          throw new Error('Anmeldung wird neu gestartet');
        }
      }
      const shouldTryRedirect = code === 'popup_window_error' || code === 'user_cancelled' || code === 'block_nested_popups' || !code;

      if (shouldTryRedirect) {
        await msalInstance.loginRedirect(loginRequest);
        throw new Error('Anmeldung wird neu gestartet');
      }

      if (!resolvedAccount) {
        await msalInstance.loginRedirect(loginRequest);
        throw new Error('Anmeldung wird neu gestartet');
      }
    }
  }

  if (!resolvedAccount) {
    await msalInstance.loginRedirect(loginRequest);
    throw new Error('Anmeldung wird neu gestartet');
  }
  let result;
  try {
    result = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: resolvedAccount,
    });
  } catch (err) {
    // Silent acquisition can fail for several reasons in modern browsers:
    //   - InteractionRequiredAuthError: refresh token expired / consent needed
    //   - BrowserAuthError "monitor_window_timeout": hidden iframe SSO blocked
    //     (third-party cookies disabled, which is the default in Chrome 2024+)
    // In all of these cases, an interactive popup is the correct fallback.
    const code = err && (err.errorCode || err.name);
    const isInteractionRequired = err instanceof InteractionRequiredAuthError;
    const isIframeTimeout = code === 'monitor_window_timeout';
    if (isInteractionRequired || isIframeTimeout) {
      result = await msalInstance.acquireTokenPopup({
        ...loginRequest,
        account: resolvedAccount,
      });
    } else {
      throw err;
    }
  }
  if (!result || !result.accessToken) {
    throw new Error('Kein Access-Token von Microsoft erhalten');
  }
  return result.accessToken;
}

function extractDetailText(payload, fallback) {
  if (!payload) return fallback;
  if (typeof payload.detail === 'string') return payload.detail;
  if (payload.detail && typeof payload.detail.message === 'string') return payload.detail.message;
  if (typeof payload.message === 'string') return payload.message;
  return fallback;
}

async function apiRequest(path, { method = 'GET', token, body } = {}) {
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  const resp = await fetch(resolveApiUrl(path), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  let payload = null;
  try {
    payload = await resp.json();
  } catch {
    payload = null;
  }

  if (!resp.ok) {
    const detail = extractDetailText(payload, `API-Fehler (${resp.status})`);
    const error = new Error(detail);
    // Der Status wird mitgereicht: manche Fehler (429) tragen keinen Code im
    // Text, sollen dem Nutzer aber trotzdem verständlich gemeldet werden.
    error.status = resp.status;
    throw error;
  }
  return payload;
}

/**
 * Upload a video File to the BPMN host and return the public URL.
 *
 * @param {File} file
 * @param {{
 *   msalInstance?: import('@azure/msal-browser').IPublicClientApplication,
 *   account?: import('@azure/msal-browser').AccountInfo,
 *   onProgress?: (loaded: number, total: number) => void,
 * }} opts
 * @returns {Promise<{ url: string, filename: string, size: number, mimeType: string }>}
 */
export function uploadVideo(file, opts = {}) {
  const { msalInstance, account, onProgress } = opts;
  return new Promise((resolve, reject) => {
    (async () => {
      let token = null;
      try {
        token = await getApiToken(msalInstance, account);
      } catch (err) {
        reject(new Error(err && err.message ? err.message : 'Anmeldung fehlgeschlagen'));
        return;
      }

      const url = resolveApiUrl('/api/dashboard/videos/upload');
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded, e.total);
        };
      }

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (typeof data.url !== 'string') {
              reject(new Error('Unerwartete Antwort vom Server'));
              return;
            }
            resolve(data);
          } catch {
            reject(new Error('Antwort konnte nicht gelesen werden'));
          }
        } else {
          let detail = `Upload fehlgeschlagen (${xhr.status})`;
          try {
            const data = JSON.parse(xhr.responseText);
            if (data && data.detail) detail = `${detail}: ${data.detail}`;
          } catch { /* ignore */ }
          reject(new Error(detail));
        }
      };
      xhr.onerror = () => reject(new Error('Netzwerkfehler beim Upload'));
      xhr.onabort = () => reject(new Error('Upload abgebrochen'));

      const form = new FormData();
      form.append('file', file, file.name);
      xhr.send(form);
    })();
  });
}

/**
 * List BPMN files from SharePoint folder.
 */
export async function listSharePointBpmnFiles(opts = {}) {
  const { msalInstance, account, path = '', q = '', limit = 100 } = opts;
  const token = await getApiToken(msalInstance, account);
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (q) params.set('q', q);
  if (limit) params.set('limit', String(limit));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const data = await apiRequest(`/api/dashboard/bpmn-sharepoint/files${suffix}`, { token });
  return Array.isArray(data?.data) ? data.data : [];
}

/**
 * Return recent BPMN files for the current user.
 */
export async function listRecentBpmnFiles(opts = {}) {
  const { msalInstance, account, limit = 10 } = opts;
  const token = await getApiToken(msalInstance, account);
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  const data = await apiRequest(`/api/dashboard/bpmn-sharepoint/recent?${params.toString()}`, { token });
  return Array.isArray(data?.data) ? data.data : [];
}

/**
 * Track a successful BPMN editor login for audit/analytics.
 */
export async function trackBpmnLoginEvent(opts = {}) {
  const { msalInstance, account } = opts;
  const token = await getApiToken(msalInstance, account);
  return apiRequest('/api/dashboard/bpmn-sharepoint/login-event', {
    method: 'POST',
    token,
  });
}

/**
 * Load a BPMN file from SharePoint by path.
 */
export async function loadSharePointBpmnFile(opts = {}) {
  const { msalInstance, account, path } = opts;
  if (!path) throw new Error('Kein SharePoint-Dateipfad angegeben');
  const token = await getApiToken(msalInstance, account);
  const params = new URLSearchParams({ path });
  return apiRequest(`/api/dashboard/bpmn-sharepoint/file?${params.toString()}`, { token });
}

/**
 * Save a BPMN file to SharePoint.
 */
export async function saveSharePointBpmnFile(opts = {}) {
  const { msalInstance, account, path, content, ifMatch } = opts;
  if (!path) throw new Error('Kein SharePoint-Dateipfad angegeben');
  if (!content) throw new Error('Kein BPMN-Inhalt angegeben');
  const token = await getApiToken(msalInstance, account);
  return apiRequest('/api/dashboard/bpmn-sharepoint/file', {
    method: 'PUT',
    token,
    body: {
      path,
      content,
      ifMatch: ifMatch || null,
    },
  });
}

/**
 * Create a temporary same-origin video session for a SharePoint link.
 */
export async function createSharePointVideoSession(opts = {}) {
  const { msalInstance, account, videoUrl } = opts;
  if (!videoUrl) throw new Error('Keine Video-URL angegeben');
  const token = await getApiToken(msalInstance, account);
  return apiRequest('/api/dashboard/bpmn-videos/session', {
    method: 'POST',
    token,
    body: { videoUrl },
  });
}


/**
 * Convert a locally picked Signavio archive (.sgx).
 *
 * A single archive usually holds several process models, so the call returns a
 * catalogue plus an import id; the chosen diagram is fetched afterwards via
 * {@link loadSgxDiagram}.
 *
 * @param {File} file
 * @param {{
 *   msalInstance?: import('@azure/msal-browser').IPublicClientApplication,
 *   account?: import('@azure/msal-browser').AccountInfo,
 *   onProgress?: (loaded: number, total: number) => void,
 * }} opts
 * @returns {Promise<{ importId: string, sourceName: string, diagrams: Array<object> }>}
 */
export function convertSgxUpload(file, opts = {}) {
  const { msalInstance, account, onProgress } = opts;
  return new Promise((resolve, reject) => {
    (async () => {
      let token = null;
      try {
        token = await getApiToken(msalInstance, account);
      } catch (err) {
        reject(new Error(err && err.message ? err.message : 'Anmeldung fehlgeschlagen'));
        return;
      }

      const xhr = new XMLHttpRequest();
      xhr.open('POST', resolveApiUrl('/api/dashboard/bpmn-sgx/convert'));
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);

      if (onProgress && xhr.upload) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onProgress(e.loaded, e.total);
        };
      }

      xhr.onload = () => {
        let data = null;
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          data = null;
        }
        if (xhr.status >= 200 && xhr.status < 300 && data && Array.isArray(data.diagrams)) {
          resolve(data);
          return;
        }
        const error = new Error(
          extractDetailText(data, `SGX-Konvertierung fehlgeschlagen (${xhr.status})`),
        );
        error.status = xhr.status;
        reject(error);
      };
      xhr.onerror = () => reject(new Error('Netzwerkfehler beim SGX-Upload'));
      xhr.onabort = () => reject(new Error('SGX-Upload abgebrochen'));

      const form = new FormData();
      form.append('file', file, file.name);
      xhr.send(form);
    })();
  });
}

/**
 * Convert a Signavio archive that already lives in SharePoint. The archive is
 * downloaded server-side — a .sgx is a ZIP and has no business travelling
 * through the browser.
 */
export async function convertSgxFromSharePoint(opts = {}) {
  const { msalInstance, account, path } = opts;
  if (!path) throw new Error('Kein SharePoint-Dateipfad angegeben');
  const token = await getApiToken(msalInstance, account);
  return apiRequest('/api/dashboard/bpmn-sgx/convert-sharepoint', {
    method: 'POST',
    token,
    body: { path },
  });
}

/**
 * Fetch one converted diagram of a previous SGX import.
 */
export async function loadSgxDiagram(opts = {}) {
  const { msalInstance, account, importId, diagramId } = opts;
  if (!importId || !diagramId) throw new Error('Kein SGX-Diagramm angegeben');
  const token = await getApiToken(msalInstance, account);
  return apiRequest(
    `/api/dashboard/bpmn-sgx/import/${encodeURIComponent(importId)}/diagram/${encodeURIComponent(diagramId)}`,
    { token },
  );
}

/**
 * Drop a SGX import server-side. Best effort — the server also expires imports
 * on its own, so a failure here is not worth surfacing.
 */
export async function discardSgxImport(opts = {}) {
  const { msalInstance, account, importId } = opts;
  if (!importId) return null;
  try {
    const token = await getApiToken(msalInstance, account);
    return await apiRequest(`/api/dashboard/bpmn-sgx/import/${encodeURIComponent(importId)}`, {
      method: 'DELETE',
      token,
    });
  } catch {
    return null;
  }
}
